/**
 * Ashlen Sticky Banner — supports two variants:
 *   - 'credit'  → triggered by URL with ?discount=JMPKA1&credit=X&expires=Y
 *   - 'sale'    → triggered by URL with ?discount=SUMMER70&expires=Y  (no credit param)
 *
 * Storage key: ashlen_active_offer (single localStorage entry, replaces ashlen_active_credit)
 * Cookie: ashlen_credit_active=1 (kept for Klaviyo popup coordination — name unchanged for backwards compat)
 *
 * Updated for credit-vs-70%-off A/B test (Jun 2026).
 */
(function () {
  'use strict';

  const STORAGE_KEY      = 'ashlen_active_offer';
  const LEGACY_KEY       = 'ashlen_active_credit'; // backwards compat — migrate on load
  const CURRENCY_SYMBOLS = { USD: '$', EUR: '€', GBP: '£' };

  // ---------- Init / migration ----------
  const params  = new URLSearchParams(window.location.search);
  const urlDisc = params.get('discount');
  const urlCred = params.get('credit');
  const urlExp  = params.get('expires');
  const urlMin  = params.get('min');
  const urlCurr = params.get('currency');

  let offer = readOffer();

  // URL params override stored offer (new email click = fresh experience)
  if (urlDisc && urlExp) {
    offer = {
      variant:  urlCred ? 'credit' : 'sale',
      discount: urlDisc,
      credit:   urlCred || null,
      expires:  urlExp,
      min:      urlMin  || null,
      currency: urlCurr || null,
      dismissed: false
    };
    writeOffer(offer);
    setCookie(urlExp);
  }

  if (!offer) return;
  if (offer.dismissed) return;
  if (new Date() >= new Date(normaliseDate(offer.expires))) {
    clearOffer();
    return;
  }

  // Pre-apply discount at cart/session level so cart reflects discount before checkout — idempotent
  if (offer.discount && !offer.discount_applied) {
    fetch('/discount/' + encodeURIComponent(offer.discount), {
      method: 'GET', credentials: 'same-origin'
    }).then(() => {
      offer.discount_applied = true;
      writeOffer(offer);
    }).catch(() => { /* silent fail; checkout-level fallback still works */ });
  }

  // ---------- Render flow ----------
  const overlaySelectors = [
    '.klaviyo-form-overlay',
    '[role="dialog"][aria-modal="true"]',
    '#rebuy-modal:not([style*="display: none"])',
    '.rebuy-cart-drawer.is-open',
    '[data-rebuy-modal][aria-hidden="false"]'
  ].join(',');

  function attemptRender() {
    const conflicting = document.querySelector(overlaySelectors);
    if (conflicting && conflicting.offsetParent !== null) {
      setTimeout(attemptRender, 500);
      return;
    }
    render(offer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attemptRender);
  } else {
    attemptRender();
  }

  // ---------- Render functions ----------
  function render(o) {
    if (document.querySelector('.credit-banner')) return; // already rendered

    const el = document.createElement('div');
    el.className = 'credit-banner credit-banner--' + o.variant;
    el.setAttribute('data-variant', o.variant);
    el.innerHTML = o.variant === 'sale' ? saleMarkup(o) : creditMarkup(o);
    document.body.appendChild(el);

    wireEvents(el, o);
    setupRebuyListeners(el);
    setupOverlayObserver(el);
    setupInternalLinkRewrite(o);
  }

  function creditMarkup(o) {
    const sym         = CURRENCY_SYMBOLS[o.currency] || '$';
    const expFriendly = formatDate(o.expires);
    const minBullet   = o.min
      ? `<li>✓ Works on orders over ${sym}${o.min}</li>`
      : '';
    return `
      <div class="credit-banner__compact">
        <div class="credit-banner__copy">
          <span class="credit-banner__amount">${sym}${o.credit}</span> credit · expires ${expFriendly}
        </div>
        <button class="credit-banner__info" aria-label="More info">ⓘ</button>
        <button class="credit-banner__close" aria-label="Dismiss">✕</button>
      </div>
      <div class="credit-banner__drawer" hidden>
        <h3 class="credit-banner__headline">Your ${sym}${o.credit} credit</h3>
        <ul class="credit-banner__bullets">
          <li>✓ Already applied — code <strong>${o.discount}</strong> enters automatically at checkout</li>
          <li>✓ Stacks with our sitewide 60% off sale</li>
          <li>✓ Buy 2, get 1 free — add code <strong>HEART</strong> to layer this on top</li>
          ${minBullet}
          <li>⏰ Expires ${expFriendly}</li>
        </ul>
        <button class="credit-banner__cta">Continue shopping →</button>
      </div>
    `;
  }

  function saleMarkup(o) {
    const expFriendly = formatDate(o.expires);
    return `
      <div class="credit-banner__compact">
        <div class="credit-banner__copy">
          <span class="credit-banner__amount">70% OFF</span> · everything · expires ${expFriendly}
        </div>
        <button class="credit-banner__info" aria-label="More info">ⓘ</button>
        <button class="credit-banner__close" aria-label="Dismiss">✕</button>
      </div>
      <div class="credit-banner__drawer" hidden>
        <h3 class="credit-banner__headline">Our biggest sale ever — 70% off</h3>
        <ul class="credit-banner__bullets">
          <li>✓ Code <strong>${o.discount}</strong> is already applied — enters automatically at checkout</li>
          <li>✓ Sitewide — no minimum, no exclusions</li>
          <li>✓ Biggest discount we've ever run — back to 60% after this</li>
          <li>⏰ Expires ${expFriendly}</li>
        </ul>
        <button class="credit-banner__cta">Continue shopping →</button>
      </div>
    `;
  }

  // ---------- Event wiring ----------
  function wireEvents(el, o) {
    const drawer   = el.querySelector('.credit-banner__drawer');
    const infoBtn  = el.querySelector('.credit-banner__info');
    const closeBtn = el.querySelector('.credit-banner__close');
    const ctaBtn   = el.querySelector('.credit-banner__cta');

    infoBtn.addEventListener('click', () => {
      drawer.hidden = !drawer.hidden;
      el.classList.toggle('credit-banner--expanded', !drawer.hidden);
    });

    closeBtn.addEventListener('click', () => {
      o.dismissed = true;
      writeOffer(o);
      clearCookie();
      el.remove();
    });

    ctaBtn.addEventListener('click', () => {
      drawer.hidden = true;
      el.classList.remove('credit-banner--expanded');
    });
  }

  function setupRebuyListeners(el) {
    const hide = () => el.classList.add('is-hidden');
    const show = () => el.classList.remove('is-hidden');
    document.addEventListener('rebuy:cart.show',  hide);
    document.addEventListener('rebuy:cart.hide',  show);
    document.addEventListener('rebuy:modal.show', hide);
    document.addEventListener('rebuy:modal.hide', show);
  }

  function setupOverlayObserver(el) {
    const observer = new MutationObserver(() => {
      const rebuyOpen = document.querySelector(
        '#rebuy-modal:not([style*="display: none"]), .rebuy-cart-drawer.is-open, [data-rebuy-modal][aria-hidden="false"]'
      );
      el.classList.toggle('is-hidden', !!rebuyOpen);
    });
    observer.observe(document.body, {
      childList: true, subtree: true, attributes: true,
      attributeFilter: ['class', 'style', 'aria-hidden']
    });
  }

  function setupInternalLinkRewrite(o) {
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a');
      if (!a || !a.href) return;
      try {
        const url = new URL(a.href);
        if (url.host !== location.host) return;
        if (!url.searchParams.has('discount')) {
          url.searchParams.set('discount', o.discount);
          a.href = url.toString();
        }
      } catch (err) { /* ignore malformed URLs */ }
    });
  }

  // ---------- Helpers ----------
  function readOffer() {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      if (stored) return stored;
      // Migration from legacy key
      const legacy = JSON.parse(localStorage.getItem(LEGACY_KEY) || 'null');
      if (legacy) {
        const migrated = {
          variant:  'credit',
          discount: legacy.discount,
          credit:   legacy.credit,
          expires:  legacy.expires,
          min:      legacy.min      || null,
          currency: legacy.currency || null,
          dismissed: !!legacy.dismissed
        };
        writeOffer(migrated);
        localStorage.removeItem(LEGACY_KEY);
        return migrated;
      }
      return null;
    } catch (e) { return null; }
  }

  function writeOffer(o) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(o)); } catch (e) {}
  }

  function clearOffer() {
    localStorage.removeItem(STORAGE_KEY);
    clearCookie();
  }

  function setCookie(expires) {
    try {
      const ttl = Math.max(0, (new Date(normaliseDate(expires)) - new Date()) / 1000);
      document.cookie = `ashlen_credit_active=1; path=/; max-age=${Math.floor(ttl)}`;
    } catch (e) {}
  }

  function clearCookie() {
    document.cookie = 'ashlen_credit_active=; path=/; max-age=0';
  }

  // Treat bare date strings (no time) as end-of-day local time — prevents same-day
  // expiry appearing broken when the server sends a plain date like "2026-06-11".
  function normaliseDate(str) {
    return str && str.indexOf('T') === -1 ? str + 'T23:59:59' : str;
  }

  function formatDate(expires) {
    try {
      const d = new Date(normaliseDate(expires));
      if (isNaN(d.getTime())) return '';
      return new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        month:   'long',
        day:     'numeric'
      }).format(d);
    } catch (e) { return ''; }
  }
})();
